import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  callOutline,
  closeOutline,
  videocamOutline
} from 'ionicons/icons';

import { CallService } from '../../core/services/call.service';
import { LiveKitRoomComponent } from './live-kit-room.component';

/**
 * Global 1:1 call overlay — mounted once at the app root so an incoming call
 * (or an outgoing/connected call) flies in over whatever page the user is on.
 * It is the mobile counterpart of the web venue's incoming-call ring +
 * private-call modal, rendered purely off `CallService.state()`.
 *
 *   - incoming   → answer sheet (Accept / Decline)
 *   - outgoing   → "Calling …" with Cancel
 *   - connecting → spinner with Cancel
 *   - connected  → full-screen `LiveKitRoomComponent`
 *
 * The component holds no call logic; every action delegates to `CallService`,
 * which owns the hub signalling and LiveKit credential exchange.
 */
@Component({
  selector: 'mob-call-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonIcon, IonSpinner, LiveKitRoomComponent],
  template: `
    @if (state(); as call) {
      @if (call.status !== 'idle') {
        <div class="call" [class.call--connected]="call.status === 'connected'" role="dialog" aria-modal="true">
          @switch (call.status) {
            @case ('connected') {
              <header class="call__bar">
                <div class="call__bar-id">
                  <strong>{{ call.peerName }}</strong>
                  <span>Connected</span>
                </div>
              </header>
              @if (call.credentials; as creds) {
                @defer (when call.status === 'connected') {
                  <mob-live-kit-room
                    class="call__room"
                    [credentials]="creds"
                    [callType]="call.callType"
                    [waitingLabel]="'Waiting for ' + call.peerName + '…'"
                    (left)="onHangUp()"
                  />
                } @placeholder {
                  <div class="call__loading"><ion-spinner name="crescent" /></div>
                }
              }
            }
            @default {
              <div class="call__ring">
                <div class="call__avatar" aria-hidden="true">
                  <ion-icon [name]="call.callType === 'audio' ? 'call-outline' : 'videocam-outline'" />
                </div>
                <h2 class="call__peer">{{ call.peerName }}</h2>
                <p class="call__sub">
                  @switch (call.status) {
                    @case ('incoming') { Incoming {{ call.callType }} call… }
                    @case ('outgoing') { Calling… }
                    @case ('connecting') { Connecting… }
                  }
                </p>

                @if (call.error) {
                  <p class="call__error" role="alert">{{ call.error }}</p>
                }

                @if (call.status === 'connecting') {
                  <ion-spinner name="crescent" />
                }

                <div class="call__actions">
                  @if (call.status === 'incoming') {
                    <button type="button" class="call__btn call__btn--decline" (click)="onDecline()" aria-label="Decline">
                      <ion-icon name="close-outline" />
                    </button>
                    <button type="button" class="call__btn call__btn--accept" (click)="onAccept()" aria-label="Accept">
                      <ion-icon [name]="call.callType === 'audio' ? 'call-outline' : 'videocam-outline'" />
                    </button>
                  } @else {
                    <button type="button" class="call__btn call__btn--decline" (click)="onHangUp()" aria-label="Cancel call">
                      <ion-icon name="call-outline" />
                    </button>
                  }
                </div>
              </div>
            }
          }
        </div>
      }
    }
  `,
  styles: [
    `
      :host { display: contents; }
      .call {
        position: fixed;
        inset: 0;
        z-index: 10000;
        background: linear-gradient(160deg, #0f172a, #1e293b);
        display: flex;
        flex-direction: column;
        color: #e2e8f0;
      }
      .call--connected { background: #0b1220; }
      .call__bar {
        flex: 0 0 auto;
        padding: calc(0.75rem + env(safe-area-inset-top)) 1rem 0.75rem;
      }
      .call__bar-id { display: flex; flex-direction: column; }
      .call__bar-id strong { font-size: 1.05rem; }
      .call__bar-id span { font-size: 0.78rem; opacity: 0.7; }
      .call__room { flex: 1 1 auto; min-height: 0; }
      .call__loading {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .call__ring {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        padding: 2rem 1.5rem calc(3rem + env(safe-area-inset-bottom));
        text-align: center;
      }
      .call__avatar {
        width: 6rem;
        height: 6rem;
        border-radius: 9999px;
        background: rgba(255, 255, 255, 0.1);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 0.5rem;
      }
      .call__avatar ion-icon { font-size: 2.75rem; color: #fff; }
      .call__peer { margin: 0; font-size: 1.5rem; font-weight: 700; }
      .call__sub { margin: 0; opacity: 0.75; text-transform: capitalize; }
      .call__error { margin: 0.25rem 0 0; color: #fca5a5; font-size: 0.85rem; }
      .call__actions {
        margin-top: 2rem;
        display: flex;
        gap: 2.5rem;
        justify-content: center;
      }
      .call__btn {
        width: 4.25rem;
        height: 4.25rem;
        border-radius: 9999px;
        border: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        transition: transform 0.12s ease;
      }
      .call__btn:active { transform: scale(0.92); }
      .call__btn ion-icon { font-size: 1.9rem; }
      .call__btn--accept { background: #22c55e; }
      .call__btn--decline { background: #ef4444; }
      .call__btn--decline ion-icon { transform: rotate(135deg); }
    `
  ]
})
export class CallOverlayComponent {
  private readonly calls = inject(CallService);
  protected readonly state = this.calls.state;

  /** Hide the page behind the call so background scroll is locked. */
  protected readonly isOpen = computed(() => this.calls.isActive());

  constructor() {
    addIcons({
      'call-outline': callOutline,
      'close-outline': closeOutline,
      'videocam-outline': videocamOutline
    });
  }

  protected onAccept(): void {
    void this.calls.accept();
  }

  protected onDecline(): void {
    void this.calls.decline();
  }

  protected onHangUp(): void {
    void this.calls.hangUp();
  }
}
